import moment from "moment";

import { TFunction } from "utils";

import {
  CROP_TYPE_FCPO,
  CropItemData,
  FertilizersResponse,
  FieldPracticeResponse,
  PRACTICE_SLUG,
  PracticeFCPO,
  PracticeFertilizerMineral,
  PracticeSlug,
  ZERO_UUID,
} from "models";

import {
  FertilizerItemData,
  FertilizerValue,
  GROUP_TYPE,
  PRACTICE_TYPE,
  TimelineCalendarItemsProps,
} from "pages/app/field-timeline/components/FieldDetailsTimeline.tsx";
import { determinePracticeEndDate, groupPracticeItemsByFourWeeks } from "pages/app/field-timeline/utils/PracticeUtils.ts";

const getFertilizerPracticeNameByApplicationType = (t: TFunction, fertilizerTypes: FertilizersResponse, type?: PracticeSlug, assignedPracticeId?: string) => {
  if (assignedPracticeId === ZERO_UUID) return;

  if (type === PRACTICE_SLUG.MINERAL_FERTILIZERS) {
    return (
      fertilizerTypes.mineral.find((mineralFertilizer: PracticeFertilizerMineral) => mineralFertilizer.id === assignedPracticeId)?.name ??
      t("fieldTimeline:cropCard.unknown")
    );
  }

  if (type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
    return (
      fertilizerTypes.organic.find((mineralFertilizer: PracticeFertilizerMineral) => mineralFertilizer.id === assignedPracticeId)?.name ??
      t("fieldTimeline:cropCard.unknown")
    );
  }
};

const createItems = (
  t: TFunction,
  groupedFertilizerItems: Map<number, PracticeFCPO[]>,
  fertilizerTypes: FertilizersResponse,
  groupType: GROUP_TYPE,
  baselineYears: { [PRACTICE_SLUG.MINERAL_FERTILIZERS]: number[]; [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: number[] },
  isMandatory: boolean
): TimelineCalendarItemsProps<FertilizerItemData>[] => {
  const fertilizerItems: TimelineCalendarItemsProps<FertilizerItemData>[] = [];
  groupedFertilizerItems.forEach((value) => {
    const fertilizerValues: FertilizerValue[] = [];
    value.forEach((practice) => {
      const isBaseline =
        practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS
          ? baselineYears[PRACTICE_SLUG.ORGANIC_FERTILIZERS].includes(practice.year!)
          : baselineYears[PRACTICE_SLUG.MINERAL_FERTILIZERS].includes(practice.year!);

      const fertilizerName = getFertilizerPracticeNameByApplicationType(t, fertilizerTypes, practice?.type, practice.assignedPracticeId);

      if (!fertilizerName) return;

      fertilizerValues.push({
        name: fertilizerName,
        endDate: practice.endDate,
        startDate: practice.startDate,
        type: practice.type,
        payout: practice.payout,
        isMandatory: isMandatory,
        isBaseline: isBaseline,
        isConfirmed: practice.isConfirmed,
      });
    });
    fertilizerItems.push({
      id: `${PRACTICE_TYPE.FERTILIZER}-${value[0].id}`,
      group: groupType,
      title: "",
      start_time: moment(value[0].endDate),
      end_time: moment(value[0].endDate).add(5, "months"),
      practiceType: PRACTICE_TYPE.FERTILIZER,
      canResize: false,
      canMove: false,
      cropItemId: value[0].cropId,
      data: {
        fertilizerValues: fertilizerValues,
      },
    });
  });

  return fertilizerItems;
};

export const createFertilizerItems = (
  t: TFunction,
  fieldCropOverViewResponse: FieldPracticeResponse,
  cropItems: TimelineCalendarItemsProps<CropItemData>[],
  fertilizerTypes: FertilizersResponse,
  baselineYears: { [PRACTICE_SLUG.MINERAL_FERTILIZERS]: number[]; [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: number[] },
  isMandatory: boolean
) => {
  let mainCropFertilizerItems: TimelineCalendarItemsProps<FertilizerItemData>[] = [];
  let sideCropFertilizerItems: TimelineCalendarItemsProps<FertilizerItemData>[] = [];

  const fertilizerList = fieldCropOverViewResponse.practices.filter(
    (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS
  );

  const mainCropFertilizerList = fertilizerList.filter((fertilizerItem) => {
    const cropType = fieldCropOverViewResponse.crops.find((crop) => crop.id === fertilizerItem.cropId)?.cropType;
    if (!cropType) {
      return true;
    }
    return cropType === CROP_TYPE_FCPO.MAIN;
  });

  const sideCropFertilizerList = fertilizerList.filter((fertilizerItem) => {
    const cropType = fieldCropOverViewResponse.crops.find((crop) => crop.id === fertilizerItem.cropId)?.cropType;

    return cropType === CROP_TYPE_FCPO.UNDERSOWING || cropType === CROP_TYPE_FCPO.COVER;
  });

  if (mainCropFertilizerList.length) {
    const groupedMainCropFertilizerItems = groupPracticeItemsByFourWeeks(mainCropFertilizerList);
    const fertilizerItems = createItems(t, groupedMainCropFertilizerItems, fertilizerTypes, GROUP_TYPE.MAIN_CROP_FERTILIZER, baselineYears, isMandatory);
    // Update the end time of each fertilizer practice to the end of the main crop or the next fertilizer practice, if available.
    mainCropFertilizerItems = fertilizerItems.map((fertilizerItem) => {
      fertilizerItem.end_time = determinePracticeEndDate(
        fertilizerItem,
        fertilizerItems,
        cropItems.filter((cropItem) => cropItem.practiceType === PRACTICE_TYPE.MAIN_CROP)
      );
      return fertilizerItem;
    });
  }

  if (sideCropFertilizerList.length) {
    const groupedSideCropFertilizerItems = groupPracticeItemsByFourWeeks(sideCropFertilizerList);
    const fertilizerItems = createItems(t, groupedSideCropFertilizerItems, fertilizerTypes, GROUP_TYPE.SIDE_CROP_FERTILIZER, baselineYears, isMandatory);
    // Update the end time of each fertilizer practice to the end of the main crop or the next fertilizer practice, if available.
    sideCropFertilizerItems = fertilizerItems.map((fertilizerItem) => {
      fertilizerItem.end_time = determinePracticeEndDate(
        fertilizerItem,
        fertilizerItems,
        cropItems.filter((cropItem) => cropItem.practiceType === PRACTICE_TYPE.UNDERSOWING_CROP || cropItem.practiceType === PRACTICE_TYPE.COVER_CROP)
      );
      return fertilizerItem;
    });
  }

  return [...mainCropFertilizerItems, ...sideCropFertilizerItems];
};
